// ═══════════════════════════════════════════════════════════════
// PublicBreakdown — renders the parts/labor/supplies line items on
// the public estimate + invoice pages. Customer-facing: every line is
// visible (no "subtotal" tricks), money in plain English. Reads
// directly from the share doc's snapshotted `lineItems` and
// `shopSupplies` fields (not from the live job).
//
// If a share doc has NO line items (legacy estimates created before
// 2026-05-13) the component renders nothing — the page falls back to
// just the description + total, which is what those older estimates
// always showed.
// ═══════════════════════════════════════════════════════════════
import React from 'react';
import { Wrench, Package } from 'lucide-react';
import {
  lineItemAmount, computeLaborSubtotal, computePartsSubtotal, formatPrice,
} from './lib/job-helpers.js';
import { formatRate } from './lib/tax-presets.js';

export default function PublicBreakdown({ share }) {
  const lineItems = Array.isArray(share?.lineItems) ? share.lineItems : [];
  const shopSupplies = Number(share?.shopSupplies) || 0;
  const labors = lineItems.filter((li) => li?.type === 'labor');
  const parts  = lineItems.filter((li) => li?.type === 'part');

  // Tax as it was frozen at send time. Absent on every document sent
  // before tax existed, and on every untaxed job.
  const tax = share?.tax || null;
  const taxTotal = Number(share?.taxTotal) || 0;
  const taxLaborAmount = Number(share?.taxLaborAmount) || 0;
  const taxPartsAmount = Number(share?.taxPartsAmount) || 0;
  const preTax = Number(share?.totalPrice) || 0;

  // Legacy share docs (no line items, no shop supplies) — show nothing
  // so the page stays clean. The grand-total block below still renders.
  if (labors.length === 0 && parts.length === 0 && shopSupplies === 0 && taxTotal === 0) {
    return null;
  }

  const laborSubtotal = computeLaborSubtotal(lineItems);
  const partsSubtotal = computePartsSubtotal(lineItems);
  const laborDetail = (li) => {
    if (!Number.isFinite(li?.hours) || !Number.isFinite(li?.hourlyRate)) return null;
    return `${li.hours} hr @ ${formatPrice(li.hourlyRate)}/hr`;
  };

  return (
    <div className="public-breakdown">
      {labors.length > 0 && (
        <section className="public-breakdown-section">
          <h3 className="public-breakdown-title">
            <Wrench size={14} /> Labor
          </h3>
          <ul className="public-breakdown-list">
            {labors.map((li) => (
              <li key={li.id} className="public-breakdown-row">
                <span className="public-breakdown-desc">
                  {li.description || '(unlabeled)'}
                  {laborDetail(li) && (
                    <span className="public-breakdown-qty muted small">
                      {' '}{laborDetail(li)}
                    </span>
                  )}
                </span>
                <span className="public-breakdown-amount mono">
                  {formatPrice(lineItemAmount(li))}
                </span>
              </li>
            ))}
          </ul>
          {labors.length > 1 && (
            <div className="public-breakdown-subtotal">
              <span>Labor subtotal</span>
              <span className="mono">{formatPrice(laborSubtotal)}</span>
            </div>
          )}
        </section>
      )}

      {parts.length > 0 && (
        <section className="public-breakdown-section">
          <h3 className="public-breakdown-title">
            <Package size={14} /> Parts
          </h3>
          <ul className="public-breakdown-list">
            {parts.map((li) => (
              <li key={li.id} className="public-breakdown-row">
                <span className="public-breakdown-desc">
                  {li.description || '(unlabeled)'}
                  {Number(li.qty) > 1 && (
                    <span className="public-breakdown-qty muted small">
                      {' '}× {li.qty}
                    </span>
                  )}
                </span>
                <span className="public-breakdown-amount mono">
                  {formatPrice(lineItemAmount(li))}
                </span>
              </li>
            ))}
          </ul>
          {parts.length > 1 && (
            <div className="public-breakdown-subtotal">
              <span>Parts subtotal</span>
              <span className="mono">{formatPrice(partsSubtotal)}</span>
            </div>
          )}
        </section>
      )}

      {shopSupplies > 0 && (
        <section className="public-breakdown-section">
          <ul className="public-breakdown-list">
            <li className="public-breakdown-row">
              <span className="public-breakdown-desc">Shop supplies</span>
              <span className="public-breakdown-amount mono">
                {formatPrice(shopSupplies)}
              </span>
            </li>
          </ul>
        </section>
      )}

      {/* Sales tax, read from what was frozen onto this document when
          it was sent — never recomputed here. A customer looking at a
          six-month-old invoice must see the rate that was charged,
          not the rate that applies today. */}
      {taxTotal > 0 && (
        <section className="public-breakdown-section public-breakdown-tax">
          <div className="public-breakdown-subtotal public-breakdown-pretax">
            <span>Subtotal</span>
            <span className="mono">{formatPrice(preTax)}</span>
          </div>
          <ul className="public-breakdown-list">
            {taxLaborAmount > 0 && (
              <li className="public-breakdown-row">
                <span className="public-breakdown-desc">
                  Sales tax on labor
                  <span className="public-breakdown-qty muted small">
                    {' '}{formatRate(tax.laborRate)}
                  </span>
                </span>
                <span className="public-breakdown-amount mono">
                  {formatPrice(taxLaborAmount)}
                </span>
              </li>
            )}
            {taxPartsAmount > 0 && (
              <li className="public-breakdown-row">
                <span className="public-breakdown-desc">
                  Sales tax on parts
                  <span className="public-breakdown-qty muted small">
                    {' '}{formatRate(tax.partsRate)}
                  </span>
                </span>
                <span className="public-breakdown-amount mono">
                  {formatPrice(taxPartsAmount)}
                </span>
              </li>
            )}
          </ul>
          {tax.label && (
            <p className="public-breakdown-tax-note muted small">{tax.label}</p>
          )}
        </section>
      )}
    </div>
  );
}
